import { type ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { type CouponCampaignView, type CouponCampaignsListView } from '@vello/api-client';
import { isIso4217Currency } from '@vello/core';
import { formatMoney } from '@vello/format';
import {
  maySelfApprove,
  EMPTY_CAMPAIGN_DRAFT,
  INCIDENT_LOOKBACK_DAYS,
  MIN_INTERVAL_HOURS,
  PER_PERSON_COUNTS,
  RATING_MAX_STARS,
  ROLLING_MAX_COUNTS,
  ROLLING_WINDOW_DAYS,
  TRIGGER_WAIT_MINUTES,
  bpToPercentText,
  campaignApproveBlock,
  campaignCanEnd,
  campaignCanRestart,
  campaignDraftIssue,
  campaignIssuedBp,
  campaignPolicyTone,
  campaignProposalBody,
  campaignStateTone,
  channelChoices,
  couponCampaignAuthority,
  couponCountOf,
  formatMarketInstant,
  isTriggerSegment,
  lookbackHourChoices,
  marketTimeZone,
  percentTextToBp,
  validityDayChoices,
  validityHourChoices,
  windowDayChoices,
  type CampaignDraft,
  tailOf,
} from '../../src/index';
import { useAdmin } from '../session';
import { usePersonNames } from '../components/person-name';
import { ScopeMarketNotice, useScopeMarket } from '../components/scope-market';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';
import { AUTO_REFRESH_MS, Badge, Button, EmptyState, Field, FormRow, Meter, MoneyInput, Notice, PageHeader, Panel, Row, Select, Stack, Table, Text, TextInput, useAutoRefresh } from '@vello/web-theme';

/**
 * Finance › Campanhas de cupom (W10-TARGETED-COUPONS, D493, ADR-0109; doc 17 section 3.3 L5), over `CouponCampaignsAdminApi` (`/v1/admin/coupon-campaigns/*`).
 *
 * For the market in the header scope (never typed):
 * - **the bounds**: whether the market publishes the campaign policy, an open fund, the coupon policy and the control-group share - NO policy, NO campaign - and the maximums the
 *   policy allows (the form never offers a value past them; the server re-checks every one);
 * - **the campaigns**: segment, terms, window in the market's own time zone, what the approval RESERVED and how much of it is issued, redeemed, still available and held out
 *   (the control group), and who proposed and decided;
 * - **FOUR EYES**: the maker proposes a campaign (a segment from the closed list - never a free-text audience, an envelope, a percent or a fixed coupon, a budget, the coupon's
 *   validity, the window, the per-person count, a reason from a closed list), a SECOND operator approves or refuses it (Approve is hidden from the proposer, with the reason
 *   shown; the approval RESERVES the budget in the fund), any operator holding the word ends an active campaign with one click (the unused remainder goes back), and an ended
 *   one is restarted only as a NEW proposal.
 * Money is typed through the design-system `MoneyInput` in the policy's currency (never minor units, never a typed currency). A refused or failed read is an ERROR - never an
 * empty list. Every code shown (segment, state, reason, refusal) is read through the central vocabulary. No native dialog is ever opened. The list refreshes itself.
 */

type Msg = { readonly tone: 'success' | 'danger' | 'warning'; readonly text: string } | undefined;

const note = (text: string): ReactElement => <EmptyState title={text} />;

export function CouponCampaignsScreen(): ReactElement {
  const { runtime, markets, me, grants } = useAdmin();
  const selfApproval = maySelfApprove(grants); // D868: a GLOBAL super administrator decides their own proposals
  const { t, lang } = useT();
  const { fail, v } = useVocab();
  const { mayView, mayManage, mayApprove } = couponCampaignAuthority(grants);
  const { market: scopeMarket, choices } = useScopeMarket();
  const market = scopeMarket ?? '';
  const [loaded, setLoaded] = useState('');
  const [view, setView] = useState<CouponCampaignsListView | undefined>(undefined);
  const personName = usePersonNames((view?.campaigns ?? []).map((c) => c.createdBy?.id));
  const [error, setError] = useState<string | undefined>(undefined);
  const [msg, setMsg] = useState<Msg>(undefined);
  const [busy, setBusy] = useState(false);
  const [why, setWhy] = useState<Record<string, string>>({});
  const [draft, setDraft] = useState<CampaignDraft>(EMPTY_CAMPAIGN_DRAFT);
  const [percentText, setPercentText] = useState('');
  const seq = useRef(0);

  const refusal = useCallback(
    (e: { code: string; reasonCode?: string; detail?: string }): string => (e.code === 'FORBIDDEN' && e.reasonCode === undefined ? t('ccamp.refusal.forbidden') : t('ccamp.error', { code: fail(e) })),
    [t, fail],
  );
  const failText = useCallback((e: { code: string; reasonCode?: string; detail?: string }): string => (e.reasonCode !== undefined || e.detail !== undefined ? fail(e) : refusal(e)), [fail, refusal]);

  const load = useCallback(
    async (m: string): Promise<void> => {
      const mine = ++seq.current;
      const target = m.trim();
      if (target === '' || !mayView) {
        setLoaded(target);
        setView(undefined);
        return;
      }
      const res = await runtime.couponCampaigns.list(target);
      if (mine !== seq.current) return;
      setLoaded(target);
      if (res.ok) {
        setView(res.value);
        setError(undefined);
      } else {
        // A refused read keeps the list unknown: the screen says it could not load, never "no campaigns".
        setError(refusal(res.error));
      }
    },
    [runtime, mayView, refusal],
  );

  useEffect(() => {
    setView(undefined);
    setError(undefined);
    setDraft(EMPTY_CAMPAIGN_DRAFT);
    setPercentText('');
    void load(market);
    // Reload when the header scope names another market.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [market]);
  useAutoRefresh(() => load(market), { intervalMs: AUTO_REFRESH_MS.queue, enabled: mayView && market !== '' && !busy });

  const tz = marketTimeZone(loaded, markets);
  const when = (iso: string | null | undefined): string => (iso === null || iso === undefined ? '—' : formatMarketInstant(iso, tz, lang));
  const money = useCallback(
    (m: { readonly amountMinor: string; readonly currency: string } | null | undefined): string =>
      m === null || m === undefined ? '—' : /^-?\d+$/.test(m.amountMinor) && isIso4217Currency(m.currency) ? formatMoney({ amountMinor: BigInt(m.amountMinor), currency: m.currency }, { locale: lang, timeZone: tz ?? 'UTC', measurement: 'metric' }) : '—',
    [lang, tz],
  );
  const policy = view?.campaignPolicy;
  const currency = policy?.currency ?? '';
  const needsOk = view !== undefined && policy?.state === 'ACTIVE' && view.fund.state === 'ACTIVE' && view.couponPolicy.state === 'ACTIVE' && view.controlGroup.state === 'ACTIVE';
  const issue = needsOk && policy !== undefined && view !== undefined ? campaignDraftIssue(draft, policy, view.proposalReasons) : undefined;
  const windows = useMemo(() => (policy !== undefined ? windowDayChoices(policy.maxWindowDays) : []), [policy]);
  const validities = useMemo(() => (policy !== undefined ? validityDayChoices(policy.maxValidityDays) : []), [policy]);
  const validityHours = useMemo(() => (policy !== undefined ? validityHourChoices(policy.maxValidityDays) : []), [policy]);
  const lookbacks = useMemo(() => lookbackHourChoices(draft.waitMinutes), [draft.waitMinutes]);
  const intOf = (value: string): number | undefined => (value === '' ? undefined : Number.parseInt(value, 10));
  const channelSets = useMemo(() => channelChoices(view?.enabledChannels ?? ['IN_APP']), [view]);
  const coupons = couponCountOf(draft);

  const propose = async (): Promise<void> => {
    if (issue !== undefined || !needsOk) return;
    setBusy(true);
    const res = await runtime.couponCampaigns.propose(campaignProposalBody(loaded, draft, new Date().toISOString()) as never);
    setBusy(false);
    if (!res.ok) return setMsg({ tone: 'danger', text: failText(res.error) });
    setDraft(EMPTY_CAMPAIGN_DRAFT);
    setPercentText('');
    setMsg({ tone: 'success', text: t('ccamp.propose.sent') });
    await load(loaded);
  };

  const decide = async (c: CouponCampaignView, decision: 'APPROVE' | 'REJECT'): Promise<void> => {
    const reason = (why[c.campaignId] ?? '').trim();
    if (decision === 'REJECT' && reason.length < 3) return setMsg({ tone: 'warning', text: t('ccamp.decision.reasonNeeded') });
    setBusy(true);
    const res = await runtime.couponCampaigns.decide(c.campaignId, { marketId: loaded, decision, ...(reason !== '' ? { reason } : {}) });
    setBusy(false);
    if (!res.ok) {
      setMsg({ tone: 'danger', text: failText(res.error) });
      await load(loaded);
      return;
    }
    setMsg({ tone: 'success', text: t(decision === 'APPROVE' ? 'ccamp.decision.approved' : 'ccamp.decision.rejected') });
    await load(loaded);
  };

  const endNow = async (c: CouponCampaignView): Promise<void> => {
    setBusy(true);
    const res = await runtime.couponCampaigns.end(c.campaignId, { marketId: loaded, reasonCode: why[`end:${c.campaignId}`] ?? 'OPERATOR_KILL_SWITCH' });
    setBusy(false);
    if (!res.ok) {
      setMsg({ tone: 'danger', text: failText(res.error) });
      await load(loaded);
      return;
    }
    setMsg({ tone: 'success', text: t('ccamp.end.done') });
    await load(loaded);
  };

  const restart = async (c: CouponCampaignView): Promise<void> => {
    const days = Math.max(1, Math.min(policy?.maxWindowDays ?? 1, Math.round((Date.parse(c.windowTo) - Date.parse(c.windowFrom)) / 86_400_000)));
    const from = new Date().toISOString();
    setBusy(true);
    const res = await runtime.couponCampaigns.restart(c.campaignId, { marketId: loaded, windowFrom: from, windowTo: new Date(Date.parse(from) + days * 86_400_000).toISOString() });
    setBusy(false);
    if (!res.ok) {
      setMsg({ tone: 'danger', text: failText(res.error) });
      await load(loaded);
      return;
    }
    setMsg({ tone: 'success', text: t('ccamp.restart.done') });
    await load(loaded);
  };

  const termsText = (c: CouponCampaignView): string =>
    c.discountKind === 'PERCENT' && c.percentBp !== null
      ? c.validityHours !== null
        ? t('ccamp.terms.percentHours', { percent: bpToPercentText(c.percentBp), cap: money(c.couponValue), hours: c.validityHours })
        : t('ccamp.terms.percent', { percent: bpToPercentText(c.percentBp), cap: money(c.couponValue), days: c.validityDays })
      : c.validityHours !== null
        ? t('ccamp.terms.fixedHours', { amount: money(c.couponValue), hours: c.validityHours })
        : t('ccamp.terms.fixed', { amount: money(c.couponValue), days: c.validityDays });

  /** D609: the trigger terms and frequency rules of a campaign, as one readable line (empty for a campaign that has none). */
  const rulesText = (c: CouponCampaignView): string =>
    [
      c.waitMinutes !== null ? t('ccamp.rule.wait', { minutes: c.waitMinutes }) : '',
      c.triggerLookbackHours !== null ? t('ccamp.rule.lookback', { hours: c.triggerLookbackHours }) : '',
      c.ratingMaxStars !== null ? t('ccamp.rule.rating', { stars: c.ratingMaxStars }) : '',
      c.minIntervalHours !== null ? t('ccamp.rule.interval', { hours: c.minIntervalHours }) : '',
      c.skipIfActiveCoupon ? t('ccamp.rule.skipActive') : '',
      c.rollingWindowDays !== null && c.rollingMaxCount !== null ? t('ccamp.rule.rolling', { count: c.rollingMaxCount, days: c.rollingWindowDays }) : '',
    ]
      .filter((x) => x !== '')
      .join(' · ');

  const actionCell = (c: CouponCampaignView): ReactElement => {
    const block = campaignApproveBlock(c, me?.id, mayApprove, selfApproval);
    if (block === undefined) {
      return (
        <Stack gap={2}>
          <TextInput aria-label={t('ccamp.decision.reason')} placeholder={t('ccamp.decision.reason')} value={why[c.campaignId] ?? ''} maxLength={500} onChange={(ev) => setWhy((r) => ({ ...r, [c.campaignId]: ev.target.value }))} />
          <Row gap={2} align="center" wrap>
            <Button type="button" variant="success" disabled={busy} onClick={() => void decide(c, 'APPROVE')}>{t('ccamp.decision.approve')}</Button>
            <Button type="button" variant="danger" disabled={busy} onClick={() => void decide(c, 'REJECT')}>{t('ccamp.decision.reject')}</Button>
          </Row>
          <Text role="caption" tone="tertiary">{t('ccamp.decision.reserves', { budget: money(c.budgetCap), count: c.couponCount })}</Text>
        </Stack>
      );
    }
    if (block === 'OWN_PROPOSAL' || block === 'NO_PERMISSION') return <Text role="caption" tone="tertiary">{block === 'OWN_PROPOSAL' ? t('ccamp.approve.own') : t('ccamp.approve.noPermission')}</Text>;
    if (campaignCanEnd(c, mayManage)) {
      return (
        <Stack gap={2}>
          <Select aria-label={t('ccamp.end.reason')} value={why[`end:${c.campaignId}`] ?? 'OPERATOR_KILL_SWITCH'} onChange={(ev) => setWhy((r) => ({ ...r, [`end:${c.campaignId}`]: ev.target.value }))}>
            {(view?.endReasons ?? []).map((r) => (
              <option key={r} value={r}>{v('coupon_campaign.reason', r)}</option>
            ))}
          </Select>
          <Button type="button" variant="danger" disabled={busy} onClick={() => void endNow(c)}>{t('ccamp.end.button')}</Button>
        </Stack>
      );
    }
    if (campaignCanRestart(c, mayManage)) {
      return (
        <Stack gap={2}>
          <Text role="caption" tone="tertiary">{c.endReason !== null ? v('coupon_campaign.reason', c.endReason) : '—'}</Text>
          <Button type="button" variant="secondary" disabled={busy} onClick={() => void restart(c)}>{t('ccamp.restart.button')}</Button>
        </Stack>
      );
    }
    return <span>{c.endReason !== null ? v('coupon_campaign.reason', c.endReason) : (c.decisionReason ?? '—')}</span>;
  };

  return (
    <>
      <PageHeader title={t('ccamp.title')} subtitle={t('ccamp.subtitle')} />
      {!mayView ? (
        <Panel>{note(t('ccamp.noPermission'))}</Panel>
      ) : (
        <>
          {(error !== undefined || msg !== undefined) && (
            <Panel>
              {error !== undefined && <Notice tone="danger">{error}</Notice>}
              {msg !== undefined && <Notice tone={msg.tone}>{msg.text}</Notice>}
            </Panel>
          )}

          {scopeMarket === undefined && <ScopeMarketNotice choices={choices} />}

          {view !== undefined && policy !== undefined && (
            <>
              <Panel>
                <Stack gap={3}>
                  <h3>{t('ccamp.policy.title')}</h3>
                  <Row gap={2} align="center" wrap>
                    <Text role="caption" tone="tertiary">{t('ccamp.policy.fund')}</Text>
                    <Badge tone={campaignPolicyTone(view.fund.state)}>{v('incentive_fund.policy_state', view.fund.state)}</Badge>
                    <Text role="caption" tone="tertiary">{t('ccamp.policy.campaign')}</Text>
                    <Badge tone={campaignPolicyTone(policy.state)}>{v('incentive_fund.policy_state', policy.state)}</Badge>
                    <Text role="caption" tone="tertiary">{t('ccamp.policy.coupon')}</Text>
                    <Badge tone={campaignPolicyTone(view.couponPolicy.state)}>{v('incentive_fund.policy_state', view.couponPolicy.state)}</Badge>
                    <Text role="caption" tone="tertiary">{t('ccamp.policy.control')}</Text>
                    <Badge tone={campaignPolicyTone(view.controlGroup.state)}>{v('incentive_fund.policy_state', view.controlGroup.state)}</Badge>
                  </Row>
                  {!needsOk ? (
                    <Notice tone="warning">{t('ccamp.policy.needs')}</Notice>
                  ) : (
                    <Text role="caption" tone="tertiary">
                      <span className="tnum">
                        {t('ccamp.policy.bounds', {
                          budget: money(policy.maxBudget), value: money(policy.maxCouponValue), percent: bpToPercentText(policy.maxPercentBp), validity: policy.maxValidityDays, window: policy.maxWindowDays,
                          month: policy.maxCouponsPerPersonPerMonth, control: bpToPercentText(view.controlGroup.controlGroupBp),
                        })}
                      </span>
                    </Text>
                  )}
                </Stack>
              </Panel>

              <Panel padded={false}>
                <Stack gap={3}>
                  <h3>{t('ccamp.list.title')}</h3>
                  <Table
                    columns={[
                      {
                        key: 'campaign', header: t('ccamp.col.campaign'),
                        render: (c: CouponCampaignView) => (
                          <Stack gap={1}>
                            <span>{v('coupon_campaign.segment', c.segment)}</span>
                            <Text role="caption" tone="tertiary"><span className="tnum">{termsText(c)} · {v('coupon_campaign.envelope', c.envelope)}</span></Text>
                            {rulesText(c) !== '' && <Text role="caption" tone="tertiary"><span className="tnum">{rulesText(c)}</span></Text>}
                            <Text role="caption" tone="tertiary">{v('coupon_campaign.reason', c.reasonCode)}</Text>
                          </Stack>
                        ),
                      },
                      { key: 'window', header: t('ccamp.col.window'), render: (c: CouponCampaignView) => <span className="tnum">{t('ccamp.window.line', { from: when(c.windowFrom), to: when(c.endedAt ?? c.windowTo) })}</span> },
                      {
                        key: 'budget', header: t('ccamp.col.budget'), numeric: true,
                        render: (c: CouponCampaignView) => (
                          <Stack gap={1}>
                            <Meter label={t('ccamp.col.budget')} value={campaignIssuedBp(c)} max={10_000} showMax={false} warnAt={0.8} dangerAt={1} formatValue={(x) => `${Math.floor(x / 100)}%`} />
                            <Text role="caption" tone="tertiary">
                              <span className="tnum">{t('ccamp.budget.line', { reserved: money(c.reserved), issued: c.issued, count: c.couponCount, redeemed: c.redeemed, remaining: c.remainingCoupons })}</span>
                            </Text>
                            <Text role="caption" tone="tertiary">
                              <span className="tnum">{t('ccamp.budget.control', { held: c.heldOut, excluded: c.excluded, confirmed: money(c.confirmed) })}</span>
                            </Text>
                          </Stack>
                        ),
                      },
                      { key: 'state', header: t('ccamp.col.state'), render: (c: CouponCampaignView) => <Badge tone={campaignStateTone(c.state)}>{v('coupon_campaign.state', c.state)}</Badge> },
                      { key: 'by', header: t('ccamp.col.by'), render: (c: CouponCampaignView) => <span>{t('ccamp.by.line', { who: c.createdBy == null ? '—' : (c.createdBy.label ?? personName(c.createdBy.id) ?? tailOf(c.createdBy.id)), when: when(c.createdAt) })}</span> },
                      { key: 'action', header: t('ccamp.col.action'), render: actionCell },
                    ]}
                    rows={view.campaigns}
                    rowKey={(c) => c.campaignId}
                    empty={note(t('ccamp.empty'))}
                  />
                </Stack>
              </Panel>

              {mayManage && (
                <Panel>
                  <Stack gap={3}>
                    <h3>{t('ccamp.propose.title')}</h3>
                    {!needsOk ? (
                      <Text role="caption" tone="tertiary">{t('ccamp.propose.needsPolicy')}</Text>
                    ) : (
                      <FormRow action={<Button type="button" disabled={issue !== undefined || busy} onClick={() => void propose()}>{t('ccamp.propose.submit')}</Button>}>
                        <Field label={t('ccamp.propose.segment')} htmlFor="camp-segment">
                          <Select id="camp-segment" value={draft.segment} onChange={(ev) => setDraft((d) => ({ ...d, segment: ev.target.value }))}>
                            <option value="">{t('ccamp.propose.choose')}</option>
                            {policy.segments.map((s) => (
                              <option key={s} value={s}>{v('coupon_campaign.segment', s)}</option>
                            ))}
                          </Select>
                        </Field>
                        {draft.segment === 'AFTER_INCIDENT' && (
                          <Field label={t('ccamp.propose.lookback')} htmlFor="camp-lookback">
                            <Select id="camp-lookback" value={draft.lookbackDays === undefined ? '' : String(draft.lookbackDays)} onChange={(ev) => setDraft((d) => ({ ...d, lookbackDays: ev.target.value === '' ? undefined : Number.parseInt(ev.target.value, 10) }))}>
                              <option value="">{t('ccamp.propose.choose')}</option>
                              {INCIDENT_LOOKBACK_DAYS.map((n) => (
                                <option key={n} value={String(n)}>{t('ccamp.propose.days', { days: n })}</option>
                              ))}
                            </Select>
                          </Field>
                        )}
                        {isTriggerSegment(draft.segment) && (
                          <>
                            <Field label={t('ccamp.propose.wait')} htmlFor="camp-wait" hint={draft.segment === 'QUOTE_NO_REQUEST' ? t('ccamp.propose.wait.hint') : undefined}>
                              <Select id="camp-wait" value={draft.waitMinutes === undefined ? '' : String(draft.waitMinutes)} onChange={(ev) => setDraft((d) => ({ ...d, waitMinutes: intOf(ev.target.value), triggerLookbackHours: undefined }))}>
                                <option value="">{draft.segment === 'QUOTE_NO_REQUEST' ? t('ccamp.propose.choose') : t('ccamp.propose.none')}</option>
                                {TRIGGER_WAIT_MINUTES.map((n) => (
                                  <option key={n} value={String(n)}>{t('ccamp.propose.minutes', { minutes: n })}</option>
                                ))}
                              </Select>
                            </Field>
                            <Field label={t('ccamp.propose.triggerLookback')} htmlFor="camp-trigger-lookback">
                              <Select id="camp-trigger-lookback" value={draft.triggerLookbackHours === undefined ? '' : String(draft.triggerLookbackHours)} onChange={(ev) => setDraft((d) => ({ ...d, triggerLookbackHours: intOf(ev.target.value) }))}>
                                <option value="">{t('ccamp.propose.choose')}</option>
                                {lookbacks.map((n) => (
                                  <option key={n} value={String(n)}>{t('ccamp.propose.hours', { hours: n })}</option>
                                ))}
                              </Select>
                            </Field>
                          </>
                        )}
                        {draft.segment === 'AFTER_LOW_RATING' && (
                          <Field label={t('ccamp.propose.rating')} htmlFor="camp-rating">
                            <Select id="camp-rating" value={draft.ratingMaxStars === undefined ? '' : String(draft.ratingMaxStars)} onChange={(ev) => setDraft((d) => ({ ...d, ratingMaxStars: intOf(ev.target.value) }))}>
                              <option value="">{t('ccamp.propose.choose')}</option>
                              {RATING_MAX_STARS.map((n) => (
                                <option key={n} value={String(n)}>{t('ccamp.propose.stars', { stars: n })}</option>
                              ))}
                            </Select>
                          </Field>
                        )}
                        <Field label={t('ccamp.propose.envelope')} htmlFor="camp-envelope">
                          <Select id="camp-envelope" value={draft.envelope} onChange={(ev) => setDraft((d) => ({ ...d, envelope: ev.target.value }))}>
                            <option value="">{t('ccamp.propose.choose')}</option>
                            {policy.envelopes.map((s) => (
                              <option key={s} value={s}>{v('coupon_campaign.envelope', s)}</option>
                            ))}
                          </Select>
                        </Field>
                        <Field label={t('ccamp.propose.kind')} htmlFor="camp-kind">
                          <Select id="camp-kind" value={draft.discountKind} onChange={(ev) => setDraft((d) => ({ ...d, discountKind: ev.target.value }))}>
                            {view.discountKinds.map((k) => (
                              <option key={k} value={k}>{v('promotions.coupon_discount_kind', k)}</option>
                            ))}
                          </Select>
                        </Field>
                        {draft.discountKind === 'PERCENT' && (
                          <Field label={t('ccamp.propose.percent')} htmlFor="camp-percent" hint={t('ccamp.propose.percent.hint', { percent: bpToPercentText(policy.maxPercentBp) })}>
                            <TextInput
                              id="camp-percent"
                              inputMode="decimal"
                              value={percentText}
                              maxLength={6}
                              onChange={(ev) => {
                                setPercentText(ev.target.value);
                                setDraft((d) => ({ ...d, percentBp: percentTextToBp(ev.target.value) }));
                              }}
                            />
                          </Field>
                        )}
                        {isIso4217Currency(currency) && (
                          <>
                            <MoneyInput
                              id="camp-value" label={t(draft.discountKind === 'PERCENT' ? 'ccamp.propose.cap' : 'ccamp.propose.amount')} currency={currency} locale={lang} valueMinor={draft.valueMinor}
                              onChangeMinor={(minor) => setDraft((d) => ({ ...d, valueMinor: minor }))} issueText={(i) => v('format.money_input_issue', i)}
                            />
                            <MoneyInput id="camp-budget" label={t('ccamp.propose.budget')} currency={currency} locale={lang} valueMinor={draft.budgetCapMinor} onChangeMinor={(minor) => setDraft((d) => ({ ...d, budgetCapMinor: minor }))} issueText={(i) => v('format.money_input_issue', i)} />
                          </>
                        )}
                        <Field label={t('ccamp.propose.validity')} htmlFor="camp-validity">
                          <Select
                            id="camp-validity"
                            value={draft.validityHours !== undefined ? `H${draft.validityHours}` : draft.validityDays !== undefined ? `D${draft.validityDays}` : ''}
                            onChange={(ev) => {
                              const x = ev.target.value;
                              setDraft((d) => ({ ...d, validityHours: x.startsWith('H') ? Number.parseInt(x.slice(1), 10) : undefined, validityDays: x.startsWith('D') ? Number.parseInt(x.slice(1), 10) : undefined }));
                            }}
                          >
                            <option value="">{t('ccamp.propose.choose')}</option>
                            {validityHours.map((n) => (
                              <option key={`H${n}`} value={`H${n}`}>{t('ccamp.propose.validity.hoursOption', { hours: n })}</option>
                            ))}
                            {validities.map((n) => (
                              <option key={`D${n}`} value={`D${n}`}>{t('ccamp.propose.days', { days: n })}</option>
                            ))}
                          </Select>
                        </Field>
                        <Field label={t('ccamp.propose.window')} htmlFor="camp-window">
                          <Select id="camp-window" value={draft.windowDays === undefined ? '' : String(draft.windowDays)} onChange={(ev) => setDraft((d) => ({ ...d, windowDays: ev.target.value === '' ? undefined : Number.parseInt(ev.target.value, 10) }))}>
                            <option value="">{t('ccamp.propose.choose')}</option>
                            {windows.map((n) => (
                              <option key={n} value={String(n)}>{t('ccamp.propose.days', { days: n })}</option>
                            ))}
                          </Select>
                        </Field>
                        <Field label={t('ccamp.propose.perPerson')} htmlFor="camp-per-person">
                          <Select id="camp-per-person" value={String(draft.perPersonCount)} onChange={(ev) => setDraft((d) => ({ ...d, perPersonCount: Number.parseInt(ev.target.value, 10) }))}>
                            {PER_PERSON_COUNTS.map((n) => (
                              <option key={n} value={String(n)}>{String(n)}</option>
                            ))}
                          </Select>
                        </Field>
                        <Field label={t('ccamp.propose.period')} htmlFor="camp-period">
                          <Select id="camp-period" value={draft.perPersonPeriod} onChange={(ev) => setDraft((d) => ({ ...d, perPersonPeriod: ev.target.value }))}>
                            {view.periods.map((p) => (
                              <option key={p} value={p}>{v('coupon_campaign.period', p)}</option>
                            ))}
                          </Select>
                        </Field>
                        <Field label={t('ccamp.propose.minInterval')} htmlFor="camp-min-interval">
                          <Select id="camp-min-interval" value={draft.minIntervalHours === undefined ? '' : String(draft.minIntervalHours)} onChange={(ev) => setDraft((d) => ({ ...d, minIntervalHours: intOf(ev.target.value) }))}>
                            <option value="">{t('ccamp.propose.none')}</option>
                            {MIN_INTERVAL_HOURS.map((n) => (
                              <option key={n} value={String(n)}>{t('ccamp.propose.hours', { hours: n })}</option>
                            ))}
                          </Select>
                        </Field>
                        <Field label={t('ccamp.propose.skipActive')} htmlFor="camp-skip-active">
                          <Select id="camp-skip-active" value={draft.skipIfActiveCoupon ? 'yes' : 'no'} onChange={(ev) => setDraft((d) => ({ ...d, skipIfActiveCoupon: ev.target.value === 'yes' }))}>
                            <option value="no">{t('ccamp.propose.no')}</option>
                            <option value="yes">{t('ccamp.propose.yes')}</option>
                          </Select>
                        </Field>
                        <Field label={t('ccamp.propose.rollingWindow')} htmlFor="camp-rolling-window">
                          <Select id="camp-rolling-window" value={draft.rollingWindowDays === undefined ? '' : String(draft.rollingWindowDays)} onChange={(ev) => setDraft((d) => ({ ...d, rollingWindowDays: intOf(ev.target.value) }))}>
                            <option value="">{t('ccamp.propose.none')}</option>
                            {ROLLING_WINDOW_DAYS.map((n) => (
                              <option key={n} value={String(n)}>{t('ccamp.propose.days', { days: n })}</option>
                            ))}
                          </Select>
                        </Field>
                        {draft.rollingWindowDays !== undefined && (
                          <Field label={t('ccamp.propose.rollingCount')} htmlFor="camp-rolling-count">
                            <Select id="camp-rolling-count" value={draft.rollingMaxCount === undefined ? '' : String(draft.rollingMaxCount)} onChange={(ev) => setDraft((d) => ({ ...d, rollingMaxCount: intOf(ev.target.value) }))}>
                              <option value="">{t('ccamp.propose.choose')}</option>
                              {ROLLING_MAX_COUNTS.map((n) => (
                                <option key={n} value={String(n)}>{String(n)}</option>
                              ))}
                            </Select>
                          </Field>
                        )}
                        <Field label={t('ccamp.propose.channels')} htmlFor="camp-channels">
                          <Select id="camp-channels" value={draft.channels.join('+')} onChange={(ev) => setDraft((d) => ({ ...d, channels: ev.target.value.split('+') }))}>
                            {channelSets.map((set) => (
                              <option key={set.join('+')} value={set.join('+')}>{set.map((c) => v('broadcast.channel', c)).join(' + ')}</option>
                            ))}
                          </Select>
                        </Field>
                        <Field label={t('ccamp.propose.reason')} htmlFor="camp-reason">
                          <Select id="camp-reason" value={draft.reasonCode} onChange={(ev) => setDraft((d) => ({ ...d, reasonCode: ev.target.value }))}>
                            <option value="">{t('ccamp.propose.choose')}</option>
                            {view.proposalReasons.map((r) => (
                              <option key={r} value={r}>{v('coupon_campaign.reason', r)}</option>
                            ))}
                          </Select>
                        </Field>
                      </FormRow>
                    )}
                    {needsOk && coupons !== undefined && <Text role="caption" tone="tertiary"><span className="tnum">{t('ccamp.propose.preview', { count: coupons })}</span></Text>}
                    {needsOk && issue !== undefined && draft !== EMPTY_CAMPAIGN_DRAFT && <Text role="caption" tone="tertiary">{t(`ccamp.issue.${issue}`)}</Text>}
                  </Stack>
                </Panel>
              )}
            </>
          )}
        </>
      )}
    </>
  );
}
